import { ConditionType } from "@types-runway/condition";
import { SidebarContentIndex } from "@types-runway/sidebarContent";
import { toSlug } from "@utils/content/toSlug";
import { CONDITION_TREATMENT_CONTENTS_URL } from "@utils/constantsEnv";

const resolveBaseUrl = (): string | null => CONDITION_TREATMENT_CONTENTS_URL ?? null;

let indexCache: SidebarContentIndex | null = null;
let indexFetchFailed = false;

export const clearSidebarContentCache = (): void => {
  indexCache = null;
  indexFetchFailed = false;
};

const fetchIndex = async (baseUrl: string): Promise<SidebarContentIndex | null> => {
  if (indexCache) return indexCache;
  if (indexFetchFailed) return null;

  try {
    const response = await fetch(`${baseUrl}index.json`);

    if (!response.ok) {
      indexFetchFailed = true;
      return null;
    }

    indexCache = (await response.json()) as SidebarContentIndex;
    return indexCache;
  } catch {
    indexFetchFailed = true;
    return null;
  }
};

const fetchHtml = async (url: string): Promise<string | null> => {
  try {
    const response = await fetch(url);

    if (!response.ok) return null;

    return await response.text();
  } catch {
    return null;
  }
};

const useSidebarContent = () => {
  const getConditionContent = async (conditionType: ConditionType): Promise<string | null> => {
    const baseUrl = resolveBaseUrl();
    if (!baseUrl) return null;

    const index = await fetchIndex(baseUrl);

    if (index) {
      const entry = index[conditionType];
      if (entry?.condition) {
        const html = await fetchHtml(`${baseUrl}${entry.condition}`);
        if (html !== null) return html;
      }
    }

    // Legacy fallback: monolithic HTML file per condition
    const html = await fetchHtml(`${baseUrl}legacy/${conditionType}.html`);
    return html ?? null;
  };

  const getTreatmentContent = async (
    conditionType: ConditionType,
    medicationName?: string
  ): Promise<string | null> => {
    const baseUrl = resolveBaseUrl();
    if (!baseUrl) return null;

    const index = await fetchIndex(baseUrl);

    if (!index) return null;

    const entry = index[conditionType];
    if (!entry) return null;

    const { treatments, defaultTreatment } = entry;

    if (Object.keys(treatments).length === 0) return null;

    const slug = medicationName !== undefined ? toSlug(medicationName) : "";
    const treatmentKey = (slug && treatments[slug]) ? slug : defaultTreatment;

    if (!treatmentKey) return "Treatment information not available.";

    const treatmentEntry = treatments[treatmentKey];
    if (!treatmentEntry) return "Treatment information not available.";

    const treatmentPath = treatmentEntry.content;
    if (!treatmentPath) return "Treatment information not available.";

    const html = await fetchHtml(`${baseUrl}${treatmentPath}`);
    return html ?? "Treatment information not available.";
  };

  const getTreatmentDescription = (
    conditionType: ConditionType,
    medicationName: string
  ): string | null => {
    if (!indexCache) return null;

    const entry = indexCache[conditionType];
    if (!entry) return null;

    const slug = toSlug(medicationName);
    const treatment = entry.treatments[slug];
    return treatment?.description ?? null;
  };

  const prefetchIndex = async (): Promise<void> => {
    const baseUrl = resolveBaseUrl();
    if (baseUrl) await fetchIndex(baseUrl);
  };

  /**
   * @deprecated Use getConditionContent() instead.
   * Kept for backward compatibility with consumers not yet migrated to the split content structure.
   */
  const getConditionDescription = async (conditionType: ConditionType): Promise<string | null> => {
    const baseUrl = resolveBaseUrl();
    if (!baseUrl) return null;

    const html = await fetchHtml(`${baseUrl}legacy/${conditionType}.html`);
    return html ?? null;
  };

  return { getConditionContent, getTreatmentContent, getTreatmentDescription, prefetchIndex, getConditionDescription };
};

export default useSidebarContent;
